import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { employeesApi, organizationApi } from '../services/api';

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
  employee_number?: string;
  role: string;
  permissions?: string[];
}

interface TimeEntry {
  id: string;
  clock_in: string;
  clock_out?: string;
}

interface EmployeeContextType {
  employee: Employee | null;
  setEmployee: (employee: Employee | null) => void;
  logout: () => void;
  isLoggedIn: boolean;
  isLocked: boolean;
  lock: () => void;
  unlock: () => void;
  resetInactivityTimer: () => void;
  // Time tracking
  isClockedIn: boolean;
  currentTimeEntry: TimeEntry | null;
  todayMinutes: number;
  clockIn: () => Promise<void>;
  clockOut: () => Promise<void>;
  refreshTimeStatus: () => Promise<void>;
  // Permissions
  hasPermission: (permission: string) => boolean;
  permissions: string[];
  // Inactivity warning
  showInactivityWarning: boolean;
  secondsUntilLock: number;
  dismissWarning: () => void;
  // Employee mode setting
  employeeModeEnabled: boolean;
  employeeModeLoading: boolean;
  // Organization timezone (account-level)
  timezone: string;
}

const EmployeeContext = createContext<EmployeeContextType | undefined>(undefined);

// Production values: 8 minutes warning, 10 minutes total
const WARNING_TIMEOUT = 8 * 60 * 1000; // 8 minutes - show warning after this
const LOCKOUT_TIMEOUT = 10 * 60 * 1000; // 10 minutes total - lock after this
const COUNTDOWN_DURATION = LOCKOUT_TIMEOUT - WARNING_TIMEOUT; // 2 minutes between warning and lockout

export const EmployeeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isLoaded: clerkLoaded, isSignedIn } = useAuth();
  const [employee, setEmployeeState] = useState<Employee | null>(null);
  const [isLocked, setIsLocked] = useState(false);
  
  // Use refs for timers to avoid stale closures
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const lockoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Inactivity warning state
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [secondsUntilLock, setSecondsUntilLock] = useState(0);
  const showWarningRef = useRef(false); // Ref to track warning state for event handlers
  
  // Time tracking state
  const [isClockedIn, setIsClockedIn] = useState(false);
  const [currentTimeEntry, setCurrentTimeEntry] = useState<TimeEntry | null>(null);
  const [todayMinutes, setTodayMinutes] = useState(0);

  // Employee mode setting (fetched from org settings)
  const [employeeModeEnabled, setEmployeeModeEnabled] = useState(false);
  const [employeeModeLoading, setEmployeeModeLoading] = useState(true);

  // Organization timezone (account-level)
  const [timezone, setTimezone] = useState('America/New_York');

  // Fetch employee mode setting once Clerk is ready and user is signed in
  useEffect(() => {
    // Wait for Clerk to be loaded and user to be signed in
    if (!clerkLoaded || !isSignedIn) {
      // Still loading or not signed in - keep loading state true
      return;
    }

    const fetchEmployeeMode = async () => {
      try {
        const settings = await organizationApi.getSettings();
        setEmployeeModeEnabled(settings.employee_mode_enabled || false);
        setTimezone(settings.timezone || 'America/New_York');
        console.log('[EmployeeContext] Employee mode enabled:', settings.employee_mode_enabled);
        console.log('[EmployeeContext] Org timezone:', settings.timezone);
      } catch (error) {
        // If fetch fails, default to disabled (new users won't see lock screen)
        console.log('[EmployeeContext] Could not fetch employee mode setting, defaulting to disabled');
        setEmployeeModeEnabled(false);
      } finally {
        setEmployeeModeLoading(false);
      }
    };
    
    // Small delay to ensure token getter is set up in App.tsx
    const timer = setTimeout(fetchEmployeeMode, 100);
    return () => clearTimeout(timer);
  }, [clerkLoaded, isSignedIn]);

  // On page load, check for active session
  // - sessionStorage: active session (persists across refresh, clears on browser close)
  // - sessionStorage: workstation_locked flag (persists locked state across refresh)
  // - localStorage: last active employee (for display on lock screen)
  useEffect(() => {
    const activeSession = sessionStorage.getItem('active_employee_session');
    const storedEmployee = localStorage.getItem('logged_in_employee');
    const wasLocked = sessionStorage.getItem('workstation_locked') === 'true';
    
    if (activeSession) {
      try {
        const emp = JSON.parse(activeSession);
        // Restore session
        setEmployeeState(emp);
        // Check if workstation was locked before refresh
        if (wasLocked) {
          setIsLocked(true);
          console.log('[EmployeeContext] Restored session with locked state for:', emp.first_name, emp.last_name);
        } else {
          setIsLocked(false);
          console.log('[EmployeeContext] Restored active session for:', emp.first_name, emp.last_name);
        }
      } catch (error) {
        console.error('Failed to parse active session:', error);
        sessionStorage.removeItem('active_employee_session');
        sessionStorage.removeItem('workstation_locked');
        // Fall back to locked state
        if (storedEmployee) {
          setIsLocked(true);
        }
      }
    } else if (storedEmployee) {
      try {
        // No active session, but we have a last-used employee
        // Show lock screen with their name
        JSON.parse(storedEmployee);
        setIsLocked(true);
        console.log('[EmployeeContext] No active session, showing lock screen');
      } catch (error) {
        console.error('Failed to parse stored employee:', error);
        localStorage.removeItem('logged_in_employee');
      }
    }
  }, []);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (lockoutTimerRef.current) {
      clearTimeout(lockoutTimerRef.current);
      lockoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    setShowInactivityWarning(false);
    showWarningRef.current = false;
    setSecondsUntilLock(0);
  }, []);

  // Reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    // Clear existing timers first
    clearAllTimers();

    // Only set timers if employee is logged in and not locked
    if (employee && !isLocked) {
      // Timer 1: Show warning after WARNING_TIMEOUT
      warningTimerRef.current = setTimeout(() => {
        console.log('[Inactivity] Showing warning after inactivity');
        setShowInactivityWarning(true);
        showWarningRef.current = true;
        setSecondsUntilLock(Math.ceil(COUNTDOWN_DURATION / 1000));
        
        // Start countdown - only one interval
        countdownIntervalRef.current = setInterval(() => {
          setSecondsUntilLock(prev => {
            if (prev <= 1) {
              if (countdownIntervalRef.current) {
                clearInterval(countdownIntervalRef.current);
                countdownIntervalRef.current = null;
              }
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      }, WARNING_TIMEOUT);

      // Timer 2: Lock after LOCKOUT_TIMEOUT
      lockoutTimerRef.current = setTimeout(() => {
        console.log('[Inactivity] Locking workstation after timeout');
        clearAllTimers();
        // Persist lock state so a hard refresh cannot bypass the lock screen.
        sessionStorage.setItem('workstation_locked', 'true');
        setIsLocked(true);
      }, LOCKOUT_TIMEOUT);
    }
  }, [employee, isLocked, clearAllTimers]);

  // Dismiss warning and reset timers (user is active)
  const dismissWarning = useCallback(() => {
    clearAllTimers();
    // Small delay before restarting timer to prevent immediate re-trigger
    setTimeout(() => {
      resetInactivityTimer();
    }, 100);
  }, [clearAllTimers, resetInactivityTimer]);

  // Track user activity
  useEffect(() => {
    if (!employee || isLocked) return;

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

    const handleActivity = () => {
      // If warning is showing and user is active, dismiss it
      // Use ref to avoid stale closure issues
      if (showWarningRef.current) {
        dismissWarning();
      } else {
        resetInactivityTimer();
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity);
    });

    // Start initial timer
    resetInactivityTimer();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      clearAllTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employee, isLocked]);

  const setEmployee = (emp: Employee | null) => {
    console.log('[EmployeeContext] setEmployee called:', emp ? `${emp.first_name} ${emp.last_name} (${emp.role})` : 'null');
    
    // Clear all timers first to prevent any race conditions
    clearAllTimers();
    
    // Update state - React batches these together
    setEmployeeState(emp);
    setIsLocked(false);
    setShowInactivityWarning(false);
    showWarningRef.current = false;
    
    if (emp) {
      // Save to both storages:
      // - sessionStorage: active session (clears on browser close)
      // - localStorage: last active employee (persists for lock screen display)
      sessionStorage.setItem('active_employee_session', JSON.stringify(emp));
      sessionStorage.removeItem('workstation_locked'); // Clear locked flag on login
      localStorage.setItem('logged_in_employee', JSON.stringify(emp));
      console.log('[EmployeeContext] Employee saved to session, permissions:', emp.permissions);
      // Timer will be started by useEffect when employee changes
    } else {
      // Clear active session
      sessionStorage.removeItem('active_employee_session');
      sessionStorage.removeItem('workstation_locked');
      // Keep localStorage for "last active" display, but could clear if preferred:
      // localStorage.removeItem('logged_in_employee');
      // Reset time tracking state when logging out
      setIsClockedIn(false);
      setCurrentTimeEntry(null);
      setTodayMinutes(0);
    }
  };

  const logout = () => {
    // Clear session storage on explicit logout
    sessionStorage.removeItem('active_employee_session');
    sessionStorage.removeItem('workstation_locked');
    setEmployee(null);
  };

  const lock = () => {
    // Keep session storage on lock - they can unlock with PIN
    // Persist locked state so it survives page refresh
    sessionStorage.setItem('workstation_locked', 'true');
    setIsLocked(true);
    setShowInactivityWarning(false);
    clearAllTimers();
  };

  const unlock = () => {
    console.log('[EmployeeContext] unlock called');
    if (isLocked) {
      // Clear the locked flag
      sessionStorage.removeItem('workstation_locked');
      setIsLocked(false);
      // Timer is started by useEffect when isLocked changes
    }
  };

  // Time tracking functions
  const refreshTimeStatus = useCallback(async () => {
    if (!employee) {
      setIsClockedIn(false);
      setCurrentTimeEntry(null);
      setTodayMinutes(0);
      return;
    }

    // Don't fetch if Clerk isn't ready yet
    if (!clerkLoaded || !isSignedIn) {
      return;
    }

    try {
      const status = await employeesApi.getTimeStatus(employee.id);
      setIsClockedIn(status.is_clocked_in);
      setCurrentTimeEntry(status.current_entry);
      setTodayMinutes(status.today_minutes);
    } catch (error) {
      console.error('Failed to get time status:', error);
    }
  }, [employee, clerkLoaded, isSignedIn]);

  const clockIn = async () => {
    if (!employee) return;

    try {
      await employeesApi.clockIn(employee.id);
      await refreshTimeStatus();
      console.log(`[TimeTracking] ${employee.first_name} clocked in`);
    } catch (error: any) {
      console.error('Failed to clock in:', error);
      throw error;
    }
  };

  const clockOut = async () => {
    if (!employee) return;

    try {
      await employeesApi.clockOut(employee.id);
      await refreshTimeStatus();
      console.log(`[TimeTracking] ${employee.first_name} clocked out`);
    } catch (error: any) {
      console.error('Failed to clock out:', error);
      throw error;
    }
  };

  // Refresh time status when employee changes (only if Clerk is ready)
  useEffect(() => {
    if (employee && clerkLoaded && isSignedIn) {
      // Small delay to ensure token getter is set up
      const timer = setTimeout(refreshTimeStatus, 150);
      return () => clearTimeout(timer);
    }
  }, [employee, clerkLoaded, isSignedIn, refreshTimeStatus]);

  // Permission check helper
  const hasPermission = (permission: string): boolean => {
    // If employee mode is disabled, grant all permissions (no restrictions)
    if (!employeeModeEnabled) return true;
    
    if (!employee) return false;
    // Admins have all permissions
    if (employee.role === 'admin') return true;
    // Check specific permission
    return employee.permissions?.includes(permission) ?? false;
  };

  // Get current permissions
  const permissions = employee?.permissions ?? [];

  const value: EmployeeContextType = {
    employee,
    setEmployee,
    logout,
    isLoggedIn: !!employee,
    isLocked,
    lock,
    unlock,
    resetInactivityTimer,
    // Time tracking
    isClockedIn,
    currentTimeEntry,
    todayMinutes,
    clockIn,
    clockOut,
    refreshTimeStatus,
    // Permissions
    hasPermission,
    permissions,
    // Inactivity warning
    showInactivityWarning,
    secondsUntilLock,
    dismissWarning,
    employeeModeEnabled,
    employeeModeLoading,
    // Organization timezone
    timezone,
  };

  return (
    <EmployeeContext.Provider value={value}>
      {children}
    </EmployeeContext.Provider>
  );
};

export const useEmployee = () => {
  const context = useContext(EmployeeContext);
  if (context === undefined) {
    throw new Error('useEmployee must be used within an EmployeeProvider');
  }
  return context;
};
